/**
 * AtlasXRModule — Quest-class VR support (workstream 6, Meta Quest 3 target).
 *
 * Wraps the engine `XRModule` with the Atlas-specific glue:
 *  - An "Enter VR" HUD button, revealed once XR capabilities detect a VR runtime
 *    (Quest browser; desktop link too, when the renderer booted WebGL2).
 *  - Rig adaptation across the session boundary: the desktop rig parks EYE_HEIGHT
 *    on the camera, but `local-floor` supplies REAL head height — so the offset
 *    (and any mouse pitch) zeroes on sessionstart and restores on sessionend,
 *    else the player floats an extra 1.6 m above the ground.
 *  - Locomotion (Input-phase system, active only while presenting):
 *      LEFT stick  → HEAD-relative move intent into atlas.intentX/Z — the exact
 *                    server-validated `move` path the keyboard uses, so the
 *                    server stays authoritative over position (Hard Rule #1).
 *      RIGHT stick → snap turn (±30°, latched at |x|>0.6, re-armed under 0.3 —
 *                    the VR-comfort default) applied to atlas.localYaw; the
 *                    headset pose composes on top of the body yaw.
 *      A button    → jump intent on the press edge (mirrors Space keydown).
 *
 * Controller state is read straight off `session.inputSources` (xr-standard
 * mapping: axes[2,3] = thumbstick, buttons[4] = A/X) — Atlas owns its raw input
 * exactly like AtlasInputModule owns the DOM; the engine InputSystem is not
 * registered in this app, so XRInputPollSystem's action-map route is idle here.
 *
 * Quest 3 serving note: WebXR needs a secure context. On-device, use
 * `adb reverse tcp:5180 tcp:5180` and browse http://localhost:5180 (localhost
 * is secure), or serve the client over HTTPS on the LAN.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { XRModule } from 'enginex/xr/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { EYE_HEIGHT } from './AtlasAvatarModule.js';
import { padFeedXr, padClearXr } from '../lib/gamepad.js';

const SNAP_TURN_RAD = Math.PI / 6; // 30° — the VR-comfort standard
const SNAP_LATCH_ON = 0.6;
const SNAP_LATCH_OFF = 0.3;
const STICK_DEADZONE = 0.15;

export class AtlasXRModule implements IModule {
  readonly name = 'AtlasXR';

  private app!: App;
  private button: HTMLButtonElement | null = null;
  private snapArmed = true;
  private prevJump = false;
  private sessionStartAt = 0;
  private lastLocoLog = 0;
  private readonly dir = new THREE.Vector3();

  constructor(private readonly xr: XRModule) {}

  init(app: App): void {
    this.app = app;

    const xrm = app.renderer.native.xr;
    xrm.addEventListener('sessionstart', () => this.onSessionStart());
    xrm.addEventListener('sessionend', () => this.onSessionEnd());

    // Locomotion poll — Input phase, so the intent is in place before the
    // FixedSim movement system (AtlasMovement) integrates + sends it.
    app.world.addSystem(makeSystem('AtlasXRLocomotion', Phase.Input, 20, () => this.poll()));
    // VR screenshots — UI phase (after the render, same XR frame): one full
    // readPixels of the XR framebuffer every SHOT interval, downsampled and
    // POSTed to the dev collector (/xr-shot → gallery at :9224/xr-shots).
    // "See what the headset sees" without devtools. ONE bulk read per
    // interval (a single pipeline sync ≈ one dropped frame) replaces the old
    // per-2s strip probe whose repeated 1-row reads were a per-capture stall
    // storm — the "glitchy when I tilt my head" hitch suspect.
    // ?xrshot=<seconds> overrides the cadence; ?xrshot=0 disables.
    app.world.addSystem(makeSystem('AtlasXRShot', Phase.UI, 50, () => this.xrScreenshot()));

    // Capabilities resolve during app.start(); world-entered fires well after
    // (a server round-trip), so the button gates correctly without polling.
    // Gate on WORLD entry, not account-ready: account-ready fires while the
    // DOM main menu is still up, and a session started there has no world and
    // no avatar rig — in-headset that is a black void (DOM never renders in
    // immersive sessions) with the controllers lost for the whole session.
    // ALSO gate on the loading screen: fixedMovement ignores intent while
    // atlas.loadingGate is up, so a session started mid-load is an unbuilt
    // world where snap turn works but the sticks don't move you (the Quest 3
    // "can turn but can't move, no buildings" field report, 2026-08-15).
    bus.on('atlas:world-entered', () => { if (!atlas.loadingGate) this.buildButton(); });
    bus.on('atlas:loading-done', () => this.buildButton());
  }

  // ─── Session boundary ─────────────────────────────────────────────────────────

  private onSessionStart(): void {
    atlas.xrPresenting = true;
    this.sessionStartAt = performance.now();
    this.snapArmed = true;
    this.prevJump = false;
    // local-floor provides real head height on the rig child; the parked
    // desktop eye offset + mouse pitch would double-apply.
    const cam = atlas.localMesh?.children[0];
    if (cam) {
      cam.position.y = 0;
      cam.rotation.x = 0;
    }
    if (this.button) this.button.textContent = 'Exit VR';
    bus.emit('atlas:status', { text: 'VR session started' });
  }

  private onSessionEnd(): void {
    atlas.xrPresenting = false;
    padClearXr();          // hand the snapshot back to the desktop poll

    atlas.intentX = 0;
    atlas.intentZ = 0;
    const cam = atlas.localMesh?.children[0];
    if (cam) cam.position.y = EYE_HEIGHT;
    if (this.button) this.button.textContent = 'Enter VR';
    // A session that dies in under 3 s is the dark-flash failure — NAME it
    // (a headset has no console; silent failure must be impossible).
    const lived = performance.now() - this.sessionStartAt;
    if (this.sessionStartAt > 0 && lived < 3000) {
      const gl = (this.app.renderer.native as unknown as { backend?: { isWebGLBackend?: boolean } }).backend?.isWebGLBackend;
      bus.emit('atlas:status', {
        text: `VR ended after ${Math.round(lived)}ms — backend ${gl ? 'WebGL2 (expected)' : 'WebGPU (XR needs WebGL2 — reload the page)'}`,
      });
    } else {
      bus.emit('atlas:status', { text: 'VR session ended' });
    }
    this.sessionStartAt = 0;
  }

  // ─── Locomotion (only while presenting) ───────────────────────────────────────

  private poll(): void {
    const xrm = this.app.renderer.native.xr;
    if (!xrm.isPresenting) return;
    const session = xrm.getSession();
    if (!session) return;

    // THE ACTIONS COME THROUGH THE SHARED PAD LAYER (2026-09-16). The headset's
    // controllers are not in `navigator.getGamepads()` — they hang off
    // `XRInputSource.gamepad` — so they are fed in and arrive as an ordinary
    // snapshot. That way the same actions and the same REBINDS work in the
    // headset as on the desk, and locomotion below stays the XR module's own.
    padFeedXr(Array.from(session.inputSources) as unknown as Parameters<typeof padFeedXr>[0]);

    let f = 0;
    let s = 0;
    let turnX = 0;
    let jump = false;

    for (const source of session.inputSources) {
      const gp = source.gamepad;
      if (!gp) continue;
      // xr-standard: axes[2,3] = thumbstick (Quest). Some runtimes expose a
      // 2-axis gamepad (stick at [0,1]) — fall back when [2,3] are absent/idle
      // so the stick is never silently dead on a nonstandard mapping.
      const ax = (i: number): number => gp.axes[i] ?? 0;
      const sx = gp.axes.length > 2 && (ax(2) !== 0 || ax(3) !== 0 || Math.abs(ax(0)) + Math.abs(ax(1)) < 0.01) ? ax(2) : ax(0);
      const sy = gp.axes.length > 2 && (ax(2) !== 0 || ax(3) !== 0 || Math.abs(ax(0)) + Math.abs(ax(1)) < 0.01) ? ax(3) : ax(1);
      if (source.handedness === 'left') {
        s = sx;
        f = -sy; // stick Y is DOWN-positive
      } else if (source.handedness === 'right') {
        turnX = sx;
        jump = gp.buttons[4]?.pressed ?? false; // A button
      }
    }

    // In-headset locomotion diagnostics, 1 Hz: a devtools-less device phones
    // these home through the beacon console ring. Field law: turn-works-but-
    // no-movement class bugs are undebuggable without per-source axis proof.
    if (performance.now() - this.lastLocoLog > 1000) {
      this.lastLocoLog = performance.now();
      // Axes arrays can carry null entries (unused touchpad slots on some
      // runtimes/emulators) — null.toFixed crashed the WHOLE poll before
      // intent was written: telemetry must never kill locomotion.
      const srcs = Array.from(session.inputSources).map((src) =>
        `${src.handedness}${src.hand ? '+hand' : ''}:${src.gamepad ? Array.from(src.gamepad.axes, (a) => (typeof a === 'number' ? a.toFixed(2) : String(a))).join(',') : 'no-gp'}`);
      console.info(`[XRLoco] f=${f.toFixed(2)} s=${s.toFixed(2)} turn=${turnX.toFixed(2)} presenting=${atlas.xrPresenting} sources=[${srcs.join(' | ')}]`);
      // [XRView] — the compositor-facing truth for the eye-misalignment class:
      // session visibility (blurred = inputs suppressed by the UA), which
      // layer type is attached, its pixel dimensions, and each eye camera's
      // viewport + position. A devtools-less headset phones these home.
      try {
        const rs = session.renderState as XRRenderState & { layers?: unknown[] };
        const base = rs.baseLayer ?? null;
        const nLayers = rs.layers?.length ?? -1;
        const xrm2 = this.app.renderer.native.xr as unknown as { getCamera?: () => { cameras: Array<{ viewport?: { x: number; y: number; width: number; height: number }; position: { x: number; y: number; z: number } }> } };
        const cams = xrm2.getCamera?.()?.cameras ?? [];
        const vps = cams.map((c, i) => {
          const v = c.viewport;
          const p = c.position;
          return `e${i}=${v ? `${v.x},${v.y},${v.width}x${v.height}` : 'no-vp'}@(${p.x.toFixed(3)},${p.y.toFixed(3)},${p.z.toFixed(3)})`;
        });
        const baseDims = base ? `${(base as XRWebGLLayer).framebufferWidth ?? '?'}x${(base as XRWebGLLayer).framebufferHeight ?? '?'}` : 'none';
        // Renderer-side sizing: canvas target dims × pixelRatio + the stored
        // renderer viewport — a mismatch against the layer dims is the
        // renderer-drew-too-small bug class, directly.
        const rn = this.app.renderer.native as unknown as {
          getSize(t: unknown): { x: number; y: number };
          getViewport(t: unknown): { x: number; y: number; z: number; w: number };
          getPixelRatio(): number;
          domElement?: { width: number; height: number };
        };
        // three's getters copy into the target via .set/.copy — hand them
        // real vectors (THREE.Vector2/4), not bare objects.
        const sz = rn.getSize(new THREE.Vector2());
        const vp2 = rn.getViewport(new THREE.Vector4()) as unknown as { x: number; y: number; z: number; w: number };
        console.info(
          `[XRView] vis=${session.visibilityState} layers=${nLayers} base=${base ? base.constructor?.name : 'null'}:${baseDims} ` +
          `rsize=${sz.x}x${sz.y}@pr${rn.getPixelRatio()} canvas=${rn.domElement?.width}x${rn.domElement?.height} ` +
          `rvp=${vp2.x},${vp2.y},${vp2.z}x${vp2.w} ${vps.join(' ')}`);
      } catch (err) {
        console.info(`[XRView] probe failed: ${(err as Error)?.message}`);
      }
      // [XRSettle] — the settlement-assembly truth from inside the session
      // ("structures never load in — VR only" field report 2026-08-16): the
      // nearest sites, what's built vs mid-assembly, and each build stage.
      try {
        const diag = (window as unknown as { __settlements?: () => { sites: number; near: Array<{ id: number; tier: string; d: number }>; built: number; building: number[]; stage: Array<[number, string]>; queue: number; catalog?: number } }).__settlements?.();
        if (diag) {
          const near = diag.near.map((n) => `${n.tier}#${n.id}@${n.d}m`).join(',');
          const stages = diag.stage.map(([id, st]) => `${id}:${st}`).join(',');
          console.info(`[XRSettle] sites=${diag.sites} near=[${near}] built=${diag.built} building=[${diag.building.join(',')}] stage=[${stages}] q=${diag.queue} cat=${diag.catalog ?? '?'}`);
        } else {
          console.info('[XRSettle] no diag hook');
        }
      } catch { /* diag shape drift — silent */ }
    }

    // Snap turn, latched so one flick = one step.
    if (this.snapArmed && Math.abs(turnX) > SNAP_LATCH_ON) {
      atlas.localYaw -= Math.sign(turnX) * SNAP_TURN_RAD;
      this.snapArmed = false;
    } else if (!this.snapArmed && Math.abs(turnX) < SNAP_LATCH_OFF) {
      this.snapArmed = true;
    }

    // Jump on the press edge (intentJump is consumed+cleared by FixedSim).
    if (jump && !this.prevJump) atlas.intentJump = true;
    this.prevJump = jump;

    // Head-relative movement: WebXRManager composes the headset pose onto the
    // rig-child camera, so its world -Z is where the player LOOKS. Project to
    // XZ and reuse the desktop intent convention (forward = (-sin, -cos)·yaw).
    if (Math.hypot(s, f) < STICK_DEADZONE) {
      atlas.intentX = 0;
      atlas.intentZ = 0;
      return;
    }
    // Head-relative when the rig camera is reachable; otherwise fall back to
    // the avatar yaw. The old `if (!cam) return` bailed WITHOUT writing intent
    // — a missing rig child silently killed all stick movement while snap turn
    // (which writes localYaw directly) kept working.
    const cam = atlas.localMesh?.children[0];
    let yawEff = atlas.localYaw;
    if (cam) {
      cam.getWorldDirection(this.dir);
      if (Math.hypot(this.dir.x, this.dir.z) > 1e-4) {
        yawEff = Math.atan2(-this.dir.x, -this.dir.z);
      }
    }
    atlas.intentX = -Math.sin(yawEff) * f + Math.cos(yawEff) * s;
    atlas.intentZ = -Math.cos(yawEff) * f - Math.sin(yawEff) * s;
  }

  // ─── VR screenshots (see-what-the-headset-sees) ───────────────────────────────

  private lastShot = 0;
  private shotInFlight = false;

  /** Capture the XR base-layer framebuffer AFTER the render and POST a
   *  downsampled JPEG to the dev collector (/xr-shot; gallery at
   *  :9224/xr-shots). ONE bulk readPixels per interval — a single pipeline
   *  sync (~one dropped frame) instead of the old strip probe's stall storm.
   *  Cadence: ?xrshot=<seconds> (default 8; 0 disables). */
  private xrScreenshot(): void {
    const xrm = this.app.renderer.native.xr;
    if (!xrm.isPresenting || this.shotInFlight) return;
    const secs = Number(new URLSearchParams(location.search).get('xrshot') ?? 8);
    if (secs <= 0) return;
    const now = performance.now();
    if (now - this.lastShot < secs * 1000) return;
    this.lastShot = now;
    try {
      const session = xrm.getSession();
      const base = session?.renderState.baseLayer;
      if (!base) return;
      const gl = (this.app.renderer.native as unknown as { getContext?: () => WebGL2RenderingContext }).getContext?.();
      if (!gl) return;
      const w = base.framebufferWidth, h = base.framebufferHeight;
      const prevRead = gl.getParameter(gl.READ_FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, base.framebuffer);
      const full = new Uint8Array(w * h * 4);
      gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, full);
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, prevRead);
      // Downsample /4 + flip (GL y-up → image y-down) off the readback.
      // ?xrshotres=2 halves instead of quarters (texture-detail forensics:
      // /4 thumbnails cannot distinguish textured-white from flat-white).
      const shift = Number(new URLSearchParams(location.search).get('xrshotres') ?? 4) === 2 ? 1 : 2;
      const dw = w >> shift, dh = h >> shift;
      const img = new ImageData(dw, dh);
      // Stride follows the shift (the hardcoded ×4 at half-res wrapped rows:
      // four ghost copies + a black bottom in the first hi-res captures).
      const step = 1 << shift;
      for (let y = 0; y < dh; y++) {
        const sy = (h - 1 - y * step) * w;
        for (let x = 0; x < dw; x++) {
          const s = (sy + x * step) * 4;
          const o = (y * dw + x) * 4;
          img.data[o] = full[s];
          img.data[o + 1] = full[s + 1];
          img.data[o + 2] = full[s + 2];
          img.data[o + 3] = 255;
        }
      }
      const cv = document.createElement('canvas');
      cv.width = dw; cv.height = dh;
      cv.getContext('2d')?.putImageData(img, 0, 0);
      this.shotInFlight = true;
      cv.toBlob((blob) => {
        if (!blob) { this.shotInFlight = false; return; }
        fetch('/xr-shot', { method: 'POST', body: blob, headers: { 'Content-Type': 'image/jpeg' } })
          .catch(() => { /* collector down — fine */ })
          .finally(() => { this.shotInFlight = false; });
      }, 'image/jpeg', 0.65);
    } catch {
      this.shotInFlight = false;
    }
  }

  // ─── Session button ───────────────────────────────────────────────────────────

  private buildButton(): void {
    if (this.button || !this.xr.capabilities.vrSupported) return;
    const b = document.createElement('button');
    b.textContent = 'Enter VR';
    // bottom:64 — the round "?" help button owns the 14px corner at z90 and
    // sat directly ON TOP of this button (the Quest overlap report).
    b.style.cssText =
      'position:fixed;right:12px;bottom:64px;z-index:40;padding:10px 16px;' +
      'background:#123a5c;color:#dff2ff;border:1px solid #2e6da4;border-radius:8px;' +
      'font:600 14px system-ui;cursor:pointer;';
    b.onclick = () => {
      void (this.xr.isPresenting ? this.xr.endSession() : this.xr.requestSession('immersive-vr'))
        .catch((err: unknown) => {
          bus.emit('atlas:status', { text: `VR session failed: ${String(err).slice(0, 120)}` });
        });
    };
    document.body.appendChild(b);
    this.button = b;
  }

  dispose(): void {
    this.button?.remove();
    this.button = null;
  }
}
